import { mdiFlash, mdiLightningBolt, mdiFire, mdiScaleBalance, mdiChartLine, mdiHeartPulse, mdiCallSplit, mdiShoePrint } from '@mdi/js';
import { workoutEnvironmentRanges, type EnvironmentRanges } from '../../utils/workoutEnvironmentRanges';
import './workout-sensor-details.css';
import { NmLoader } from '../../components/NmLoader';
import { workoutDisplayDigits as displayDigits } from '../../utils/workoutNumberFormat';
import { RunningWorkoutStatistics } from '../../components/RunningWorkoutStatistics';
import type { RunningMetrics } from '../../utils/runningWorkoutMetrics';
import type { TrainingLoadMeta } from '../../services/api';
import { workoutStressLabel } from '../../utils/workoutStressLabel';
import React, { useState } from 'react';
import { isRunningWorkout, paceFromKmh } from '../../utils/runningWorkoutMetrics';
import { useTranslation } from 'react-i18next';
import { IoChevronDownOutline } from 'react-icons/io5';
import { secToHMS } from '../../utils/secToHMS';
import { formatWorkoutDistanceM } from '../../utils/workoutStream';
import type { WorkoutDetail } from '../../services/api';
import { WsZoneStrip } from './WorkoutSummaryComponents';
import { PerformanceGuideModal, type PerformanceGuideKey, type PerformanceGuideMetric } from './PerformanceMetricGuide';

export interface HighlightStats {
  running_metrics?: RunningMetrics | null;
  training_load?: TrainingLoadMeta | null;
  title?: string;
  total_time?: number;
  distance?: number;
  tss?: number | string;
  work?: number;
  normalizedPower?: number;
  pwhr?: number | string;
  leftBalance?: number;
  grade?: number | string;
  vam?: number | string;
  if?: number | string;
  vi?: number | string;
  ef?: number | string;
  total_ascent?: number;
  total_descent?: number;
  elevationGainLost?: { gain?: number; lost?: number };
  wKg?: number | string;
  min_max_avg?: {
    heart_rate?: { min?: number; avg?: number; max?: number };
    power?: { min?: number; avg?: number; max?: number };
    cadence?: { min?: number; avg?: number; max?: number };
    speed?: { min?: number; avg?: number; max?: number };
    respiration_rate?: { min?: number | null; avg?: number | null; max?: number | null };
  };
}

function fmt(v: unknown, suffix = ''): string {
  if (v == null || v === '') return '—';
  const n = Number(v);
  if (Number.isFinite(n)) return `${Math.round(n * 10) / 10}${suffix}`;
  return `${v}${suffix}`;
}

function ExtraField({ title, value, guideKey, iconPath, color = '#e91e63' }: { title: string; value: string; guideKey?: PerformanceGuideKey; iconPath?: string; color?: string }) {
  const [guideMetric, setGuideMetric] = useState<PerformanceGuideMetric | null>(null);
  const content = <><span className="wm-extra-field__title">{iconPath && <svg className="wm-extra-field__icon" style={{ color }} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d={iconPath} /></svg>}{title}</span><span className="wm-extra-field__value ltr-island">{displayDigits(value)}</span></>;
  if (!guideKey || !iconPath) return <div className="wm-extra-field">{content}</div>;
  return (
    <>
      <button type="button" className="wm-extra-field wm-extra-field--guide" onClick={() => setGuideMetric({ key: guideKey, iconPath, color, label: title, val: value.replace(/\s(?:W\/kg|kJ|m\/h|W|m|%)$/, ''), unit: value.match(/(?:W\/kg|kJ|m\/h|W|m|%)$/)?.[0] })} aria-label={`${title}: ${value}`}>
        {content}
      </button>
      <PerformanceGuideModal metric={guideMetric} onClose={() => setGuideMetric(null)} />
    </>
  );
}

function MamRow({
  title,
  unit,
  min,
  avg,
  max,
}: {
  title: string;
  unit: string;
  min: string;
  avg: string;
  max: string;
}) {
  return (
    <div className="wm-mam-row">
      <div className="wm-mam-row__label">
        <span>{title}</span>
        <span className="wm-mam-row__unit ltr-island">{unit}</span>
      </div>
      <span className="wm-mam-row__min ltr-island">{displayDigits(min)}</span>
      <span className="wm-mam-row__avg ltr-island">{displayDigits(avg)}</span>
      <span className="wm-mam-row__max ltr-island">{displayDigits(max)}</span>
    </div>
  );
}

export function WorkoutMapBottomSheet({
  mode,
  workout,
  highlight,
  loading,
  rangeStartSec,
  rangeEndSec,
  sheetLevel,
  onToggleSheet,
  embedded = false,
  hideZones = false,
  environmentRanges,
}: {
  mode: 1 | 3;
  workout: WorkoutDetail;
  highlight?: HighlightStats | null;
  loading?: boolean;
  rangeStartSec?: number;
  rangeEndSec?: number;
  sheetLevel: 0 | 1 | 2;
  onToggleSheet: () => void;
  embedded?: boolean;
  hideZones?: boolean;
  environmentRanges?: EnvironmentRanges;
}) {
  const { t } = useTranslation();
  const mma = mode === 1 ? workout.details?.min_max_avg : highlight?.min_max_avg;
  const environment = environmentRanges ?? workoutEnvironmentRanges(mode === 1 ? workout.details?.fit_metrics : null);

  const title =
    mode === 1
      ? t('workoutDetail.map.workoutStatistics')
      : loading
        ? t('workoutDetail.map.calculatingSelectedRange')
        : highlight?.title || t('workoutDetail.map.selectedRange');

  const rangeLabel = mode === 3 && rangeStartSec != null && rangeEndSec != null
    ? `${secToHMS(rangeStartSec)} – ${secToHMS(rangeEndSec)}`
    : null;

  const totalTime = mode === 1 ? workout.total_time : highlight?.total_time;
  const distance = mode === 1 ? workout.distance : highlight?.distance;
  const tss = mode === 1 ? workout.tss_actual : highlight?.tss;

  const leftBal =
    mode === 1
      ? workout.left_balance != null
        ? Math.round(Number(workout.left_balance))
        : null
      : highlight?.leftBalance != null
        ? Math.round(Number(highlight.leftBalance))
        : null;

  const hrZones = mode === 1 ? workout.details?.zones?.hr_zones ?? [] : [];
  const pwZones = mode === 1 ? workout.details?.zones?.pw_zones ?? [] : [];
  const hasZones = hrZones.length > 0 || pwZones.length > 0;

  return (
    <div
      className={`${embedded ? "wm-sheet--embedded " : ""}wm-sheet wm-accordion-card wm-sheet--level-${sheetLevel}${sheetLevel > 0 ? ' wm-accordion-card--expanded' : ''}${loading ? ' wm-sheet--loading' : ''}`}
      aria-busy={loading || undefined}
    >
      {!embedded && <button
        type="button"
        className="wm-sheet__handle wm-accordion-card__trigger"
        onClick={onToggleSheet}
        aria-expanded={sheetLevel > 0}
        aria-label={rangeLabel ? `${title}, ${rangeLabel}` : title}
      >
        <div className="wm-sheet__handle-row">
          <span className="wm-sheet__handle-title">{title}</span>
          {rangeLabel ? <span className="wm-sheet__handle-range ltr-island">{displayDigits(rangeLabel)}</span> : null}
          <span className="wm-sheet__handle-hint">{t(sheetLevel > 0 ? 'workoutDetail.map.hideStatistics' : 'workoutDetail.map.showStatistics')}</span>
        </div>
        <div className="wm-sheet__handle-actions">
          <span className="wm-sheet__chev wm-accordion-card__chevron" aria-hidden>
            <IoChevronDownOutline />
          </span>
        </div>
      </button>}

      <div className="wm-sheet__body">
        {loading && <NmLoader size="sm" inline label={title} />}
        {isRunningWorkout(workout) ? <RunningWorkoutStatistics workout={workout} selected={mode === 3} range={highlight} environmentRanges={environment} /> : <>
        <div className="wm-sheet__hero-trio">
          <div className="wm-sheet__hero-item">
            <span className="wm-sheet__hero-lbl">{t('workoutDetail.metrics.time')}</span>
            <span className="wm-sheet__hero-val ltr-island">{displayDigits(totalTime ? secToHMS(Number(totalTime)) : '—')}</span>
          </div>
          <div className="wm-sheet__hero-item">
            <span className="wm-sheet__hero-lbl">{t('workoutDetail.metrics.distance')}</span>
            <span className="wm-sheet__hero-val ltr-island">
              {displayDigits(formatWorkoutDistanceM(distance != null ? Number(distance) : null))}
            </span>
          </div>
          <div className="wm-sheet__hero-item">
            <span className="wm-sheet__hero-lbl" style={{ textTransform: 'none' }}>{workoutStressLabel(workout)}</span>
            <span className="wm-sheet__hero-val ltr-island">{displayDigits(fmt(tss))}</span>
          </div>
        </div>

        <section className="wm-sheet__metric-section">
          <h4 className="wm-sheet__section-title">{t('workoutDetail.map.performanceMetrics')}</h4>
          <div className="wm-sheet__extras">
          <ExtraField title={t('analysis.performanceGuide.metrics.if.label')} guideKey="if" iconPath={mdiFlash} color="#f2b544" value={fmt(mode === 1 ? workout.if_actual : highlight?.if)} />
          <ExtraField title={t('analysis.performanceGuide.metrics.normalizedPower.label')} guideKey="normalizedPower" iconPath={mdiLightningBolt} color="#d66bff" value={fmt(mode === 1 ? workout.np : highlight?.normalizedPower, ' W')} />
          <ExtraField title={t('analysis.performanceGuide.metrics.work.label')} guideKey="work" iconPath={mdiFire} color="#ff7a45" value={fmt(mode === 1 ? workout.work : highlight?.work, ' kJ')} />
          <ExtraField title={t('analysis.performanceGuide.metrics.wkg.label')} guideKey="wkg" iconPath={mdiScaleBalance} color="#f2b544" value={fmt(mode === 1 ? workout.wkg : highlight?.wKg, ' W/kg')} />
          <ExtraField title={t('analysis.performanceGuide.metrics.variability.label')} guideKey="variability" iconPath={mdiChartLine} color="#61bfff" value={fmt(mode === 1 ? workout.vi : highlight?.vi)} />
          <ExtraField title={t('analysis.performanceGuide.metrics.ef.label')} guideKey="ef" iconPath={mdiHeartPulse} color="#26b979" value={fmt(mode === 1 ? workout.ef : highlight?.ef)} />
          <ExtraField title={t('analysis.performanceGuide.metrics.pwhr.label')} guideKey="pwhr" iconPath={mdiCallSplit} color="#fa8096" value={fmt(mode === 1 ? workout.pwhr : highlight?.pwhr)} />
          <ExtraField
            title={t('workoutDetail.map.footBalance')}
            iconPath={mdiShoePrint} color="#62d4c5"
            value={leftBal != null ? `${leftBal} - ${100 - leftBal}` : '—'}
          />
          </div>
        </section>

        <section className="wm-sheet__metric-section">
          <h4 className="wm-sheet__section-title">{t('workoutDetail.map.terrainMetrics')}</h4>
          <div className="wm-sheet__extras">
          <ExtraField
            title={t('workoutDetail.metrics.ascent')}
            value={fmt(
              mode === 1
                ? workout.total_ascent
                : highlight?.elevationGainLost?.gain ?? highlight?.total_ascent,
              ' m',
            )}
          />
          <ExtraField
            title={t('workoutDetail.metrics.descent')}
            value={fmt(
              mode === 1
                ? workout.total_descent
                : highlight?.elevationGainLost?.lost ?? highlight?.total_descent,
              ' m',
            )}
          />
          <ExtraField title={t('workoutDetail.metrics.grade')} value={fmt(mode === 1 ? workout.grade : highlight?.grade)} />
          <ExtraField title={t('workoutDetail.metrics.vam')} value={fmt(mode === 1 ? workout.vam : highlight?.vam, ' m/h')} />
          </div>
        </section>

        <h4 className="wm-sheet__section-title wm-sheet__section-title--table">{t('workoutDetail.map.sensorRanges')}</h4>
        <div className="wm-sheet__mam-head">
          <span />
          <span>{t('workoutDetail.summary.min')}</span>
          <span>{t('workoutDetail.summary.avg')}</span>
          <span>{t('workoutDetail.summary.max')}</span>
        </div>

        <MamRow
          title={t('workoutDetail.map.heartRate')}
          unit="bpm"
          min={fmt(mma?.heart_rate?.min)}
          avg={fmt(mma?.heart_rate?.avg)}
          max={fmt(mma?.heart_rate?.max)}
        />
        <MamRow
          title={t('workoutDetail.map.power')}
          unit="W"
          min={fmt(mma?.power?.min)}
          avg={fmt(mma?.power?.avg)}
          max={fmt(mma?.power?.max)}
        />
        <MamRow
          title={t(isRunningWorkout(workout) ? 'runningMetrics.cadence' : 'workoutDetail.map.cadence')}
          unit={isRunningWorkout(workout) ? t('runningMetrics.cadenceUnit') : 'rpm'}
          min={fmt(mma?.cadence?.min)}
          avg={fmt(mma?.cadence?.avg)}
          max={fmt(mma?.cadence?.max)}
        />
        {mma?.respiration_rate && <MamRow title={t('runningMetrics.respiration_rate')} unit={t('runningMetrics.breathsPerMinute')} min={fmt(mma.respiration_rate.min)} avg={fmt(mma.respiration_rate.avg)} max={fmt(mma.respiration_rate.max)} />}
        <MamRow
          title={t('workoutDetail.map.speed')}
          unit="km/h"
          min={fmt(mma?.speed?.min, '')}
          avg={fmt(mma?.speed?.avg, '')}
          max={fmt(mma?.speed?.max, '')}
        />
        {isRunningWorkout(workout) ? (
          <MamRow
            title={t('runningMetrics.paceLabel')}
            unit={t('runningMetrics.paceUnit')}
            min={paceFromKmh(mma?.speed?.max)}
            avg={paceFromKmh(mma?.speed?.avg)}
            max={paceFromKmh(mma?.speed?.min)}
          />
        ) : null}


        {environment.altitude && <MamRow title={t('workoutDetail.map.seriesElevation')} unit="m" min={fmt(environment.altitude.min)} avg={fmt(environment.altitude.avg)} max={fmt(environment.altitude.max)} />}
        {environment.temperature && <MamRow title={t('workoutDetail.map.airTemperature', 'Air temperature')} unit="°C" min={fmt(environment.temperature.min)} avg={fmt(environment.temperature.avg)} max={fmt(environment.temperature.max)} />}
        </>}
        {hasZones && !hideZones ? (
          <section className="wm-sheet__zones">
            {hrZones.length > 0 ? (
              <WsZoneStrip
                title={t('workoutDetail.summary.hrZones')}
                zones={hrZones}
                icon={<span aria-hidden>♥</span>}
              />
            ) : null}
            {pwZones.length > 0 ? (
              <WsZoneStrip
                title={t('workoutDetail.summary.pwZones')}
                zones={pwZones}
                icon={<span aria-hidden>⚡</span>}
              />
            ) : null}
          </section>
        ) : null}
      </div>
    </div>
  );
}
